import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    User,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPopup,
    getAdditionalUserInfo,
    signOut,
    onAuthStateChanged,
    setPersistence,
    browserLocalPersistence,
    browserSessionPersistence,
    updateProfile,
    sendPasswordResetEmail
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase/config';
import toast from 'react-hot-toast';
import { setAnalyticsUser, trackEvent } from '../services/analytics';
import { queryClient } from '../services/queryClient';
import { PROGRESS_QUERY_KEY } from '../hooks/progressCache';
import { clearNotesCache } from '../services/cacheService';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    error: string | null;
    login: (email: string, password: string, remember: boolean) => Promise<void>;
    signup: (email: string, password: string, name: string) => Promise<void>;
    loginWithGoogle: () => Promise<void>;
    logout: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    showAuthModal: boolean;
    setShowAuthModal: (show: boolean) => void;
    pendingAction: (() => void) | null;
    setPendingAction: (action: (() => void) | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setUser(user);
            setLoading(false);
            setAnalyticsUser(user?.uid ?? null);
        });

        return unsubscribe;
    }, []);

    const login = async (email: string, password: string, remember: boolean) => {
        try {
            setError(null);
            await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
            await signInWithEmailAndPassword(auth, email, password);
            trackEvent('login', { method: 'password' });
            setShowAuthModal(false);
            toast.success('Successfully logged in!');

            // Execute pending action if any
            if (pendingAction) {
                pendingAction();
                setPendingAction(null);
            }
        } catch (err: any) {
            const errorMessage = err.code === 'auth/user-not-found'
                ? 'No user found with this email'
                : err.code === 'auth/wrong-password'
                    ? 'Incorrect password'
                    : err.code === 'auth/invalid-email'
                        ? 'Invalid email address'
                        : 'Failed to login. Please try again.';

            setError(errorMessage);
            toast.error(errorMessage);
            throw err;
        }
    };

    const signup = async (email: string, password: string, name: string) => {
        try {
            setError(null);
            const { user } = await createUserWithEmailAndPassword(auth, email, password);

            // Update the user's display name
            await updateProfile(user, { displayName: name });
            trackEvent('sign_up', { method: 'password' });

            setShowAuthModal(false);
            toast.success('Account created successfully!');

            // Execute pending action if any
            if (pendingAction) {
                pendingAction();
                setPendingAction(null);
            }
        } catch (err: any) {
            const errorMessage = err.code === 'auth/email-already-in-use'
                ? 'An account with this email already exists'
                : err.code === 'auth/weak-password'
                    ? 'Password should be at least 6 characters'
                    : err.code === 'auth/invalid-email'
                        ? 'Invalid email address'
                        : 'Failed to create account. Please try again.';

            setError(errorMessage);
            toast.error(errorMessage);
            throw err;
        }
    };

    const loginWithGoogle = async () => {
        try {
            setError(null);
            await setPersistence(auth, browserLocalPersistence);
            const result = await signInWithPopup(auth, googleProvider);
            trackEvent(getAdditionalUserInfo(result)?.isNewUser ? 'sign_up' : 'login', { method: 'google' });
            setShowAuthModal(false);
            toast.success('Successfully logged in with Google!');

            // Execute pending action if any
            if (pendingAction) {
                pendingAction();
                setPendingAction(null);
            }
        } catch (err: any) {
            const errorMessage = err.code === 'auth/popup-closed-by-user'
                ? 'Login cancelled'
                : 'Failed to login with Google. Please try again.';

            setError(errorMessage);
            if (err.code !== 'auth/popup-closed-by-user') {
                toast.error(errorMessage);
            }
            throw err;
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);
            queryClient.removeQueries({ queryKey: PROGRESS_QUERY_KEY });
            clearNotesCache();
            toast.success('Successfully logged out!');
        } catch (err: any) {
            toast.error('Failed to logout. Please try again.');
            throw err;
        }
    };

    const resetPassword = async (email: string) => {
        try {
            setError(null);
            await sendPasswordResetEmail(auth, email);
            toast.success('Password reset email sent! Check your inbox.');
        } catch (err: any) {
            // Firebase handles user existence check, so we show a generic message
            const errorMessage = err.code === 'auth/invalid-email'
                ? 'Invalid email address'
                : 'If an account exists with this email, a reset link will be sent.';

            setError(errorMessage);
            // Don't show error toast for security reasons - don't reveal if user exists
            throw err;
        }
    };

    const value: AuthContextType = {
        user,
        loading,
        error,
        login,
        signup,
        loginWithGoogle,
        logout,
        resetPassword,
        showAuthModal,
        setShowAuthModal,
        pendingAction,
        setPendingAction
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 